import { cn } from '@lib/utils';
import type { Bucket } from '@utils/trl-buckets.util';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type ValueRange = { max: number; min: number };

type AreaBarChartItem = {
  onClick?: () => void;
  subtitle?: string;
  title?: string;
  valueRange: ValueRange;
};

type AreaBarChartComponentProps = {
  buckets?: Bucket[];
  customLabels?: string[];
  items: AreaBarChartItem[];
  valueRange: ValueRange;
};

const BUCKET_BG_COLORS = [
  'var(--trl-bucket-discovery-bg)',
  'var(--trl-bucket-development-bg)',
  'var(--trl-bucket-deployment-bg)',
] as const;

// Bucket header badge base classes (without bg color - added per bucket)
const BUCKET_BADGE_BASE_CLASSES =
  'flex-1 text-center font-semibold text-xs py-1.5 px-2 rounded-sm text-gray-700 border border-gray-300';

const BUCKET_GAP = 8; // gap-x-2
const COLUMN_GAP = 4; // gap-x-1
const BORDER_WIDTH = 1;

export const AreaBarChartComponent: React.FC<AreaBarChartComponentProps> = (props) => {
  const { buckets, customLabels, items, valueRange } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = (): void => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const itemHeight = 70;
  const axisBuffer = 100;
  const backgroundHeight = items.length * itemHeight + axisBuffer;

  const useBucketMode = buckets && buckets.length > 0;
  const columnCount = valueRange.max - valueRange.min + 1;

  const bucketLayout = useMemo(() => {
    if (!useBucketMode || !buckets || containerWidth === 0) return null;

    const numBuckets = buckets.length;
    const columnsPerBucket = columnCount / numBuckets;

    const totalBucketGaps = (numBuckets - 1) * BUCKET_GAP;
    const totalColumnGaps = numBuckets * (columnsPerBucket - 1) * COLUMN_GAP;
    const totalBorders = numBuckets * 2 * BORDER_WIDTH;
    const availableWidth = containerWidth - totalBucketGaps - totalColumnGaps - totalBorders;
    const columnWidth = availableWidth / columnCount;

    return { columnWidth, columnsPerBucket, numBuckets };
  }, [buckets, columnCount, containerWidth, useBucketMode]);

  const getBucketIndex = (colIndex: number): number => {
    if (!bucketLayout) return 0;

    return Math.floor(colIndex / bucketLayout.columnsPerBucket);
  };

  const getColumnLeft = (colIndex: number): number => {
    if (!bucketLayout) return 0;

    const bucketIndex = getBucketIndex(colIndex);
    const columnInBucket = colIndex % bucketLayout.columnsPerBucket;
    const bucketWidth =
      bucketLayout.columnsPerBucket * bucketLayout.columnWidth +
      (bucketLayout.columnsPerBucket - 1) * COLUMN_GAP +
      2 * BORDER_WIDTH;

    return (
      bucketIndex * (bucketWidth + BUCKET_GAP) + BORDER_WIDTH + columnInBucket * (bucketLayout.columnWidth + COLUMN_GAP)
    );
  };

  const getColumnsWidth = (colIndex: number, numColumns: number): number => {
    if (!bucketLayout) return 0;

    let width = numColumns * bucketLayout.columnWidth;
    let internalGaps = numColumns - 1;
    const startBucket = getBucketIndex(colIndex);
    const endBucket = getBucketIndex(colIndex + numColumns - 1);

    for (let b = startBucket; b < endBucket; b++) {
      width += BUCKET_GAP + 2 * BORDER_WIDTH;
      internalGaps -= 1;
    }

    return width + internalGaps * COLUMN_GAP;
  };

  // Calculate pixel positions for bars in bucket mode
  const calculateBarPosition = (colIndex: number, numColumns: number): { left: number; width: number } | null => {
    if (!bucketLayout) return null;

    return {
      left: getColumnLeft(colIndex),
      width: getColumnsWidth(colIndex, numColumns),
    };
  };

  const renderBucketHeaders = (): React.ReactNode => {
    if (!useBucketMode || !buckets) return null;

    return (
      <div className="flex w-full mb-2 gap-x-2">
        {buckets.map((bucket, index) => (
          <div
            key={index}
            className={BUCKET_BADGE_BASE_CLASSES}
            style={{ backgroundColor: BUCKET_BG_COLORS[index] }}
          >
            {bucket.label}
          </div>
        ))}
      </div>
    );
  };

  const renderBucketColumns = (): React.ReactNode => {
    if (!useBucketMode || !buckets) return null;

    return buckets.map((bucket, bucketIndex) => {
      const bgColor = BUCKET_BG_COLORS[bucketIndex];
      const columnsInBucket: React.ReactNode[] = [];

      for (let i = bucket.range.min; i <= bucket.range.max; i++) {
        const bottomLabel =
          customLabels && i - valueRange.min < customLabels.length ? customLabels[i - valueRange.min] : i.toString();

        columnsInBucket.push(
          <div
            key={i}
            className="flex-1 h-full"
            style={{ backgroundColor: bgColor }}
          >
            <div className="flex flex-col justify-end items-center h-full py-2.5 text-gray-700 text-xs">
              <div className="text-center">{bottomLabel}</div>
            </div>
          </div>,
        );
      }

      return (
        <div
          key={bucketIndex}
          className="flex flex-1 gap-x-1 rounded-sm border border-gray-300 overflow-hidden bg-white"
        >
          {columnsInBucket}
        </div>
      );
    });
  };

  const renderClassicColumns = (): React.ReactNode => {
    return new Array(columnCount).fill(0).map((_, index) => {
      const displayNumber = valueRange.min + index;
      const bottomLabel = customLabels && index < customLabels.length ? customLabels[index] : displayNumber.toString();

      return (
        <div
          key={index}
          className="w-full h-full rounded-sm bg-gray-100"
        >
          <div className="flex flex-col justify-between items-center h-full py-2.5 text-gray-700 text-xs">
            <div>{displayNumber}</div>
            <div className="text-center">{bottomLabel}</div>
          </div>
        </div>
      );
    });
  };

  const renderBars = (): React.ReactNode => {
    return items.map((item, itemIndex) => {
      const colIndex = item.valueRange.min - valueRange.min;
      const numColumns = item.valueRange.max - item.valueRange.min + 1;
      const barPosition = calculateBarPosition(colIndex, numColumns);

      if (useBucketMode && barPosition) {
        return (
          <div
            key={`bar-${itemIndex}`}
            className={cn(
              'absolute rounded-sm inline-block bg-[#E3E0FF] overflow-hidden',
              item.onClick && 'cursor-pointer',
            )}
            onClick={item.onClick}
            style={{
              height: `${itemHeight - 4}px`,
              left: `${barPosition.left}px`,
              top: `${itemIndex * itemHeight + axisBuffer / 2}px`,
              width: `${barPosition.width}px`,
            }}
          />
        );
      }

      // Fallback percentage-based positioning
      const startPercent = ((item.valueRange.min - valueRange.min) / columnCount) * 100;
      const widthPercent = ((item.valueRange.max - item.valueRange.min + 1) / columnCount) * 100;

      return (
        <div
          key={`bar-${itemIndex}`}
          className={cn(
            'absolute rounded-sm inline-block bg-[#E3E0FF] overflow-hidden',
            item.onClick && 'cursor-pointer',
          )}
          onClick={item.onClick}
          style={{
            height: `${itemHeight - 4}px`,
            left: `${startPercent}%`,
            top: `${itemIndex * itemHeight + axisBuffer / 2}px`,
            width: `${widthPercent}%`,
          }}
        />
      );
    });
  };

  const renderBarLabels = (): React.ReactNode => {
    return items.map((item, itemIndex) => {
      const colIndex = item.valueRange.min - valueRange.min;
      const numColumns = item.valueRange.max - item.valueRange.min + 1;
      const barPosition = calculateBarPosition(colIndex, numColumns);
      const rightAlign = item.valueRange.min >= valueRange.max - 2;

      if (useBucketMode && barPosition) {
        return (
          <div
            key={`bar-${itemIndex}-text`}
            className="absolute pointer-events-none"
            style={{
              left: `${barPosition.left}px`,
              padding: '12px 0',
              top: `${itemIndex * itemHeight + axisBuffer / 2}px`,
              width: rightAlign ? `${barPosition.width}px` : 'auto',
            }}
          >
            <div className={cn('flex flex-col', rightAlign ? 'items-end mr-2' : 'ml-2')}>
              {item.subtitle && (
                <div
                  className={cn('text-[#3D2EFFE5] text-xs font-normal whitespace-nowrap', rightAlign && 'text-right')}
                  style={{ direction: rightAlign ? 'rtl' : 'ltr' }}
                >
                  {item.subtitle}
                </div>
              )}
              <div
                className={cn('text-[#3D2EFF] text-sm font-semibold whitespace-nowrap', rightAlign && 'text-right')}
                style={{
                  direction: rightAlign ? 'rtl' : 'ltr',
                  unicodeBidi: 'plaintext',
                }}
              >
                {item.title}
              </div>
            </div>
          </div>
        );
      }

      // Fallback percentage-based positioning
      const startPercent = ((item.valueRange.min - valueRange.min) / valueRange.max) * 100;
      const widthPercent = ((item.valueRange.max - item.valueRange.min + valueRange.min) / valueRange.max) * 100;

      return (
        <div
          key={`bar-${itemIndex}-text`}
          className="absolute pointer-events-none"
          style={{
            left: `${startPercent}%`,
            padding: '12px 0',
            top: `${itemIndex * itemHeight + axisBuffer / 2}px`,
            width: rightAlign ? `${widthPercent}%` : 'auto',
          }}
        >
          <div className={cn('flex flex-col', rightAlign ? 'items-end mr-2' : 'ml-2')}>
            {item.subtitle && (
              <div
                className={cn('text-[#3D2EFFE5] text-xs font-normal whitespace-nowrap', rightAlign && 'text-right')}
                style={{ direction: rightAlign ? 'rtl' : 'ltr' }}
              >
                {item.subtitle}
              </div>
            )}
            <div
              className={cn('text-[#3D2EFF] text-sm font-semibold whitespace-nowrap', rightAlign && 'text-right')}
              style={{
                direction: rightAlign ? 'rtl' : 'ltr',
                unicodeBidi: 'plaintext',
              }}
            >
              {item.title}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div
      className="w-full"
      ref={containerRef}
    >
      {renderBucketHeaders()}
      <div
        className="flex gap-x-2 relative"
        style={{ height: `${backgroundHeight}px` }}
      >
        {useBucketMode ? renderBucketColumns() : renderClassicColumns()}
        {renderBars()}
        {renderBarLabels()}
      </div>
    </div>
  );
};
